<template>
  <div class="theme-config">
    <div class="config-panel">
      <h3 class="panel-title">{{ $t('aspect.manageTheme') }}</h3>
      <p class="panel-desc">{{ $t('aspect.chooseColor') }}</p>

      <div class="form-section">
        <label class="form-label">
          <span class="label-text">{{ $t('aspect.logoBrand') }}</span>

          <div v-if="whitelabel_has_logo && !is_trying_upload" class="logo-preview-container">
            <b-img :src="verifyAsset(user_data.whitelabel_settings.logo)" alt="Logo Preview" class="logo-preview w-100" @error="handleImageError"/>
          </div>
          <div v-if="(is_trying_upload)">
            <input-drag-and-drop-file
            :data="{
              extensions_allowed: '.jpg, .jpeg, .png, .webp', 
              icon: {type: 'b-icon', name: 'file-earmark-image'}, 
              title: 'Sube tu logo', 
            }"
              :max_size_mb="5"
              @file_loaded="onLogoChange"
              class="input-drag-and-drop-file"
              ></input-drag-and-drop-file>
              <small class="hint-text">{{ $t('aspect.formats') }}</small>
            </div>
          <b-button v-if="whitelabel_has_logo" variant="primary" @click="is_trying_upload = !is_trying_upload">{{is_trying_upload ? $t('aspect.seeLogoSaved') : $t('aspect.uploadNewLogo')}}</b-button>
        </label>

        <label class="form-label">
          <span class="label-text">{{ $t('aspect.brandName') }}</span>
          <input
            type="text"
            v-model.trim="brand_name"
            class="text-input"
            maxlength="80"
            :placeholder="$t('aspect.brandName')"
          >
        </label>

        <label class="form-label">
          <span class="label-text">{{ $t('aspect.primaryColor') }}</span>
          <input 
            type="color" 
            v-model="primaryColor" 
            class="color-input"
            @input="generateAndApply"
          >
          <span class="color-value">{{ primaryColor }}</span>
        </label>

        <label class="form-label">
          <span class="label-text">
            {{ $t('aspect.textOnPrimary') }}
            <small>{{ $t('aspect.optionalCalculation') }}</small>
          </span>
          <div class="toggle-auto">
            <input type="checkbox" @change="generateAndApply" v-model="autoTextColor" id="autoText">
            <label for="autoText">{{ $t('aspect.calculeAuto') }}</label>
          </div>
          <input 
            v-if="!autoTextColor"
            type="color" 
            v-model="textOnPrimary" 
            class="color-input"
            @input="generateAndApply"
          >
          <span v-if="!autoTextColor" class="color-value">{{ textOnPrimary }}</span>
        </label>

        <div class="form-label">
          <span class="label-text">
            {{ $t('aspect.backgroundHeader') }}
            <small>{{ $t('aspect.optionalUsePrimaryDefault') }}</small>
          </span>
          <div class="header-mode-group">
            <b-form-group>
              <b-form-radio-group
                v-model="headerMode"
                :options="headerModeOptions"
                name="radios-stacked"
                stacked
                @input="generateAndApply"
                click.stop
              ></b-form-radio-group>
            </b-form-group>
          </div>
          
          <div class="header-config " @click.stop>
        
            <div v-if="headerMode === 'gradient'" class="gradient-config">
              <!-- buttons action gradient -->
              <div class="gradient-mode-tabs">
                <b-button 
                  :variant="gradientMode === 'builder' ? 'primary' : 'outline-primary'"
                  @click="gradientMode = 'builder'"
                  class="w-100"
                >
                  {{ $t('aspect.builder') }}
                </b-button>
                <b-button 
                  :variant="gradientMode === 'custom' ? 'primary' : 'outline-primary'"
                  @click="gradientMode = 'custom'"
                  class="w-100"
                >
                  {{ $t('aspect.cssCustom') }}
                </b-button>
              </div>

              <div v-if="gradientMode === 'builder'">
                <div class="gradient-inputs mb-1">
                  <div class="gradient-color-group">
                    <label class="gradient-label">{{ $t('aspect.startColor') }}</label>
                    <input 
                      type="color" 
                      v-model="gradientColor1" 
                      class="color-input-small"
                      @input="generateAndApply"
                    >
                    <span class="color-value">{{ gradientColor1 }}</span>
                  </div>
                  
                  <div class="gradient-color-group">
                    <label class="gradient-label">{{ $t('aspect.endColor') }}</label>
                    <input 
                      type="color" 
                      v-model="gradientColor2" 
                      class="color-input-small"
                      @input="generateAndApply"
                    >
                    <span class="color-value">{{ gradientColor2 }}</span>
                  </div>
                </div>
                
                <div class="gradient-direction">
                  <label class="gradient-label">{{ $t('aspect.direction') }}</label>
                  <b-form-select
                    v-model="gradientDirection"
                    :options="gradientDirectionOptions"
                    class="gradient-select"
                    @change="generateAndApply"
                  />
                </div>
              </div>

              <div v-else class="gradient-custom-input">
                <label class="gradient-label">
                  {{ $t('aspect.payCss') }}
                  <small v-html="$t('aspect.gradientHintHtml')"></small>
                  <small v-html="$t('aspect.gradientExampleHtml')"></small>
                </label>
                <textarea 
                  v-model="customGradient" 
                  @input="onCustomGradientChange"
                  placeholder="linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                  rows="3"
                  class="gradient-textarea"
                ></textarea>
                <small class="hint-text">
                  {{ $t('aspect.youCanCopyGradients') }}:
                  <a href="https://cssgradient.io/" target="_blank">cssgradient.io</a>, 
                  <a href="https://uigradients.com/" target="_blank">uigradients.com</a>
                </small>
              </div>
              
              <div class="gradient-preview" :style="{background: getGradientValue()}"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="actions">
        <button type="button" class="btn btn-primary" @click="saveTheme">
          {{ $t('aspect.applyTheme') }}
        </button>
        <button type="button" class="btn btn-secondary" @click="resetTheme">
          {{ $t('aspect.resetDefault') }}
        </button>
      </div>
    </div>

    <div class="preview-panel">
      <h3 class="panel-title mb-2">{{ $t('aspect.livePreview') }}</h3>
      
      <div class="preview-section">
        <h4>{{ $t('aspect.headerPreview') }} {{ headerMode === 'gradient' ? `(${ $t('aspect.withGradient') })` : '' }}</h4>
        <div class="preview-header">
          <div class="header-logo-container">
            <img v-if="logo_preview" :src="logo_preview" alt="Logo" class="header-logo" @error="handleImageError">
            <span v-else class="header-logo-placeholder">Logo</span>
          </div>


          <div class="d-flex align-items-center">
            <b-icon icon="headset" class="mr-1" font-scale="1.3"/>
            <feather-icon
              icon="BellIcon"
              size="21"
            />
            <div class="d-flex flex-column text-right ml-1">
              <strong>{{user_data ? user_data.first_name || user_data.first_name : ""}}</strong>
              <div class="avenir-light">{{ getNameRol}} </div>
            </div>
            <b-avatar
              size="40"
              :src="verifyAsset(user_data.profile_image)"
              variant="light-primary"
              badge
              class="badge-minimal ml-1"
              badge-variant="success"
            >
              <feather-icon v-if="user_data && user_data.profile_image === null" icon="UserIcon" size="22" class="text-white" />
            </b-avatar>
          </div>
        </div>
      </div>
      <div class="preview-section">
        <h4>{{ $t('aspect.buttons') }}</h4>
        <button class="preview-btn preview-btn--primary">
          {{ $t('aspect.primaryButton') }}
        </button>
        <button class="preview-btn preview-btn--outline">
          {{ $t('aspect.outlineButton') }}
        </button>
      </div>

      <div class="preview-section">
        <h4>{{ $t('aspect.badge') }}</h4>
        <span class="preview-badge">{{ $t('aspect.badge') }}</span>
        <span class="preview-badge preview-badge--light">{{ $t('aspect.badgeLight') }}</span>
      </div>

      <div class="preview-section">
        <h4>{{ $t('aspect.input') }}</h4>
        <input type="text" class="preview-input" :placeholder="$t('aspect.focusToSeeEfect')">
      </div>

      <div class="preview-section">
        <h4>{{ $t('aspect.card') }}</h4>
        <div class="preview-card">
          <div class="preview-card__header">{{ $t('aspect.cardHeader') }}</div>
          <div class="preview-card__body">
            {{ $t('aspect.contentHeader') }}
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { 
  BAvatar, 
  BImg, 
  BButton,
  BFormGroup,
  BFormRadioGroup,
  BFormSelect,
} from 'bootstrap-vue';
import { showToast } from '@/libs/utils/others';
import { getUserData, 
  updateWhitelabelSettings
} from '@/libs/utils/user';
import { verifyAsset } from '@/libs/utils/urls'
import { updateWhitelabelTheme, getLogo } from '@/services/settings';

export default {
  name: 'AccountSettingAspect',
  components: {
    BAvatar,
    BImg,
    BButton,
    BFormGroup,
    BFormRadioGroup,
    BFormSelect,
    InputDragAndDropFile: () => import('@/views/components/inputs/inputDragAndDropFile.vue')
  },
  data() {
    return {
      verifyAsset,
      logo_file: null,
      headerModeItems: [
        { value: 'primary', labelKey: 'aspect.usePrimaryColor' },
        { value: 'gradient', labelKey: 'aspect.useGradient' },
      ],
      primaryColor: '#7367f0',
      textOnPrimary: '#ffffff',
      headerBg: '#7367f0',
      brand_name: '',
      autoTextColor: true,
      headerMode: 'primary', // 'primary' | 'gradient'
      gradientMode: 'builder', // 'builder' | 'custom'
      gradientColor1: '#7367f0',
      gradientColor2: '#28c76f',
      gradientDirection: 'to right',
      gradientDirectionOptions: [
        { value: 'to right', text: 'Horizontal →' },
        { value: 'to left', text: 'Horizontal ←' },
        { value: 'to bottom', text: 'Vertical ↓' },
        { value: 'to top', text: 'Vertical ↑' },
        { value: '135deg', text: 'Diagonal ↘' },
        { value: '45deg', text: 'Diagonal ↗' },
      ],
      customGradient: '',
      exportedJson: '',
      themeEngine: null,
      generateFullThemeFromUserInput: null,
      is_trying_upload: false,
      user_data: getUserData() || {},
    }
  },
  computed: {
    headerModeOptions() {
      return this.headerModeItems.map(item => ({
        value: item.value,
        text: this.$t(item.labelKey),
      }))
    },
    getNameRol() {
      const name = `${this.$t(`creator.displayNameMembership.${this.plan.display_name}`)}`
      return name;
    },
    plan() {
      return getUserData()?.current_plan || {};
    },
    whitelabel_has_logo() {
      return this.user_data?.whitelabel_settings?.logo || false
    },
    logo_preview() {
      return this.user_data?.whitelabel_settings?.logo || null;
    }
  },
  async mounted() {
    try {
      // load theme engine module
      const mod = await import('@/libs/theme-engine')
      this.themeEngine = mod.themeEngine
      this.generateFullThemeFromUserInput = mod.generateFullThemeFromUserInput
      
      // load saved theme
      await this.loadSavedTheme()
    } catch (e) {
      console.error('Error cargando theme engine:', e)
    }

    if (!this.whitelabel_has_logo) {
      this.is_trying_upload = true;
    }
  },
  methods: {
    handleImageError(event) {
      getLogo().then(response => {
        this.user_data.whitelabel_settings.logo = response.logo_url;
        updateWhitelabelSettings(this.user_data.whitelabel_settings);
        event.target.src = response.logo_url;
      }).catch(() => {
        event.target.src = require('@/assets/images/logo/logo-brandme.png');
      });
    },
    async loadSavedTheme() {
      const user_data = getUserData()

      // If there are whitelabel_settings saved in localStorage
      if (user_data?.whitelabel_settings?.uuid) {
        const rawSettings = user_data.whitelabel_settings.settings
        let savedInput = {}

        if (typeof rawSettings === 'string') {
          try {
            savedInput = JSON.parse(rawSettings) || {}
          } catch (error) {
            console.warn('Could not parse stored theme settings, using defaults.', error)
            savedInput = {}
          }
        } else if (rawSettings && typeof rawSettings === 'object') {
          savedInput = rawSettings
        }

        this.primaryColor = savedInput.primary || '#7367f0'

        if (savedInput.textOnPrimary) {
          this.autoTextColor = false
          this.textOnPrimary = savedInput.textOnPrimary
        } else {
          this.autoTextColor = true
          this.textOnPrimary = '#ffffff'
        }

        this.brand_name = savedInput.brand_name
          || savedInput.brandName
          || user_data.whitelabel_settings.brand_name
          || user_data.whitelabel_settings.name
          || ''

        this.restoreGradientSettings(savedInput)

        const themeInput = {
          primary: this.primaryColor,
          textOnPrimary: this.autoTextColor ? null : this.textOnPrimary,
          headerBg: this.headerMode === 'gradient' ? this.getGradientValue() : null,
        }

        const fullTheme = this.generateFullThemeFromUserInput(themeInput)
        this.themeEngine.applyTheme(fullTheme, true);

      } else {
        // Otherwise, apply default theme
        this.generateAndApply()
      }
    },
    saveTheme() {
      const userInput = this.generateAndApply()
      const form_data = new FormData();

      form_data.append('name', this.brand_name || 'Brand');
      form_data.append('settings', JSON.stringify(userInput));
      if (this.logo_file) {
        form_data.append('logo', this.logo_file);
      }

      updateWhitelabelTheme(form_data).then((response) => {
        // update whitelabel prop in user_data localStorage
        if (response?.uuid) {
          updateWhitelabelSettings(response)
          this.brand_name = response?.name || this.brand_name
          showToast(this, 'success', 'CheckIcon', 'Tema guardado correctamente')
          // emit event to force re-render of logo in navbar
          this.$root.$emit('whitelabel_logo_updated', response.logo)
          this.user_data = getUserData();
        }
      }).catch(() => {
        showToast(this, 'danger', 'XIcon', 'Error al guardar el tema')
      })      
    },
    generateAndApply() {
      if (!this.generateFullThemeFromUserInput || !this.themeEngine) return
      
      // apply header value based on gradient mode
      let headerValue = null
      if (this.headerMode === 'gradient') {
        headerValue = this.getGradientValue()
      }
      
      // build user input object
      const userInput = {
        primary: this.primaryColor,
        textOnPrimary: this.autoTextColor ? null : this.textOnPrimary,
        headerBg: headerValue,
        headerMode: this.headerMode,
        gradientMode: this.headerMode === 'gradient' ? this.gradientMode : 'primary',
        brand_name: this.brand_name,
      }

      // include gradient settings
      if (this.headerMode === 'gradient') {
        if (this.gradientMode === 'builder') {
          userInput.gradientBuilder = {
            color1: this.gradientColor1,
            color2: this.gradientColor2,
            direction: this.gradientDirection,
          }
        } else if (this.gradientMode === 'custom') {
          const sanitized = this.sanitizeGradientInput(this.customGradient)
          if (sanitized) {
            this.customGradient = sanitized
            userInput.customGradient = sanitized
          } else if (this.customGradient) {
            userInput.customGradient = this.customGradient
          }
        }
      }

      const fullTheme = this.generateFullThemeFromUserInput({
        primary: userInput.primary,
        textOnPrimary: userInput.textOnPrimary,
        headerBg: userInput.headerBg,
      })

      // apply theme
      this.themeEngine.applyTheme(fullTheme, true)

      return userInput
    },
    // restores gradient UI state from saved theme settings.
    // Handles both builder mode (color1/color2/direction) and custom CSS mode.
    restoreGradientSettings(savedInput = {}) {
      const storedHeaderMode = savedInput.headerMode
      if (storedHeaderMode) {
        this.headerMode = storedHeaderMode
      } else if (typeof savedInput.headerBg === 'string' && savedInput.headerBg.includes('gradient')) {
        this.headerMode = 'gradient'
      } else {
        this.headerMode = 'primary'
      }

      if (this.headerMode !== 'gradient') {
        this.gradientMode = 'builder'
        if (savedInput.customGradient) {
          this.customGradient = this.sanitizeGradientInput(savedInput.customGradient) || savedInput.customGradient
        }
        return
      }

      const savedGradientMode = savedInput.gradientMode || (savedInput.customGradient ? 'custom' : 'builder')
      this.gradientMode = savedGradientMode

      if (savedGradientMode === 'custom') {
        const customValue = savedInput.customGradient || savedInput.headerBg || ''
        this.customGradient = this.sanitizeGradientInput(customValue) || customValue
      } else {
        const builder = savedInput.gradientBuilder || {}
        const parsed = this.parseGradientForBuilder(savedInput.headerBg)
        this.gradientColor1 = builder.color1 || parsed.color1 || this.gradientColor1
        this.gradientColor2 = builder.color2 || parsed.color2 || this.gradientColor2
        this.gradientDirection = builder.direction || parsed.direction || this.gradientDirection

        const customValue = savedInput.customGradient || ''
        this.customGradient = customValue ? (this.sanitizeGradientInput(customValue) || customValue) : ''
      }
    },
    // parse gradient string into builder components (color1, color2, direction)
    parseGradientForBuilder(gradient) {
      const fallback = {
        direction: this.gradientDirection,
        color1: this.gradientColor1,
        color2: this.gradientColor2,
      }

      if (!gradient || typeof gradient !== 'string') {
        return fallback
      }

      const match = gradient.match(/linear-gradient\s*\((.+)\)/i)
      if (!match) {
        return fallback
      }

      const args = this.splitGradientArgs(match[1])
      if (!args.length) {
        return fallback
      }

      let direction = fallback.direction
      let colorStartIndex = 0
      const firstArg = args[0].trim()

      if (/^(to\s|[-+]?\d+(?:\.\d+)?(?:deg|rad|turn))/i.test(firstArg)) {
        direction = firstArg
        colorStartIndex = 1
      }

      const color1 = args[colorStartIndex]?.trim() || fallback.color1
      const color2 = args[colorStartIndex + 1]?.trim() || fallback.color2

      return {
        direction,
        color1,
        color2,
      }
    },
    // split gradient arguments considering nested parentheses
    splitGradientArgs(argumentString = '') {
      const result = []
      let current = ''
      let depth = 0

      for (const char of argumentString) {
        if (char === '(') {
          depth += 1
        } else if (char === ')') {
          depth = Math.max(depth - 1, 0)
        }

        if (char === ',' && depth === 0) {
          result.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }

      if (current.trim()) {
        result.push(current.trim())
      }

      return result
    },
    // Cleans and validates gradient CSS input, extracting only valid linear/radial-gradient syntax
    sanitizeGradientInput(raw) {
      if (!raw) return ''
      let input = raw.trim()
      // Normalize spaces
      input = input.replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ').trim()
      
      const gradientMatches = input.match(/(?:linear|radial)-gradient\([^;]+\)/gi)
      if (gradientMatches && gradientMatches.length) {
        return gradientMatches[gradientMatches.length - 1].trim()
      }
      // Remove background: prefix
      if (/^background:/i.test(input)) {
        input = input.replace(/^background:\s*/i, '').replace(/;$/,'').trim()
      }
      // If contains gradient, return as is without trailing semicolons
      if (/gradient\(/i.test(input)) {
        return input.replace(/;$/,'').trim()
      }
      return ''
    },
    // Returns the current gradient value based on mode (builder generates from inputs, custom uses textarea)
    getGradientValue() {
      if (this.gradientMode === 'custom' && this.customGradient.trim()) {
        const sanitized = this.sanitizeGradientInput(this.customGradient)
        if (sanitized) return sanitized
      }

      return `linear-gradient(${this.gradientDirection}, ${this.gradientColor1}, ${this.gradientColor2})`
    },
    // Validates and applies custom gradient input when user types in the textarea
    onCustomGradientChange() {
      if (!this.customGradient.trim()) return;
      
      // sanitize input avoid invalid css and apply
      const sanitized = this.sanitizeGradientInput(this.customGradient)
      if (sanitized) {
        if (sanitized !== this.customGradient.trim()) {
          this.customGradient = sanitized
        }
        this.generateAndApply()
      }
    },
    onLogoChange(file) {
      if (!file) return;
      this.logo_file = file;
    },
    resetTheme() {
      if (!this.themeEngine) return
      this.themeEngine.resetTheme()
      this.logo_file = null
      this.logo_preview = null
      if (this.$refs.logoInput) {
        this.$refs.logoInput.value = ''
      }
      this.primaryColor = '#7367f0'
      this.textOnPrimary = '#ffffff'
      this.headerBg = '#7367f0'
      this.autoTextColor = true
      this.headerMode = 'primary'
      this.gradientMode = 'builder'
      this.gradientColor1 = '#7367f0'
      this.gradientColor2 = '#28c76f'
      this.gradientDirection = 'to right'
      this.customGradient = ''
      this.exportedJson = ''
      this.brand_name = this.user_data?.whitelabel_settings?.brand_name
        || this.user_data?.whitelabel_settings?.name
        || ''
    },
  },
}
</script>

<style scoped lang="scss">
.logo-preview-container {
  width: 100%;
  height: 150px;
  background-color: var(--gray-100, #f5f5f5);
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}
.badge-minimal {
  img {
    width: 100%;
    height: auto;
  }
}
.input-drag-and-drop-file {
  height: 150px !important;
}
.theme-config {
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 2rem;
  padding: 2rem;
  min-height: 100vh;
}

.config-panel, .preview-panel {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.panel-title {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.panel-desc {
  margin: 0 0 1.5rem;
  color: #666;
  font-size: 0.9rem;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.label-text {
  font-size: 0.85rem;
  font-weight: 500;
  color: #333;
}

.label-text small {
  color: #999;
  font-weight: 400;
}

.placeholder-text {
  font-size: 0.85rem;
  font-weight: 500;
}

.color-input {
  width: 100%;
  height: 50px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
}

.text-input {
  width: 100%;
  height: 46px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.text-input:focus {
  outline: none;
  border-color: var(--brand-primary, #7367f0);
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
}

.color-value {
  font-family: monospace;
  font-size: 0.75rem;
  color: #666;
}

.toggle-auto {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
}

.header-config {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.gradient-config {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 8px;
}

.gradient-mode-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.mode-tab {
  flex: 1;
  padding: 0.5rem 1rem;
  border: 2px solid #e0e0e0;
  background: white;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.mode-tab:hover {
  border-color: var(--brand-primary, #7367f0);
  background: #f5f5f5;
}

.mode-tab.active {
  border-color: var(--brand-primary, #7367f0);
  background: var(--brand-primary, #7367f0);
  color: white;
}

.gradient-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.gradient-color-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.gradient-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #666;
}

.color-input-small {
  width: 100%;
  height: 40px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
}

.gradient-direction {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.gradient-select {
  width: 100%;
  padding: 0.6rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.85rem;
  background: white;
  cursor: pointer;
}

.gradient-select:focus {
  outline: none;
  border-color: var(--brand-primary, #7367f0);
}

.gradient-custom-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.gradient-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  resize: vertical;
  transition: border-color 0.2s;
}

.gradient-textarea:focus {
  outline: none;
  border-color: var(--brand-primary, #7367f0);
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
}

.gradient-textarea::placeholder {
  color: #999;
  font-style: italic;
}

.hint-text {
  font-size: 0.7rem;
  color: #666;
  line-height: 1.4;
}

.hint-text a {
  color: var(--brand-primary, #7367f0);
  text-decoration: none;
  font-weight: 500;
}

.hint-text a:hover {
  text-decoration: underline;
}

.gradient-preview {
  width: 100%;
  height: 60px;
  border-radius: 6px;
  border: 2px solid #e0e0e0;
  box-shadow: inset 0 1px 4px rgba(0,0,0,0.1);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.btn {
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  border: none;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--btn-primary-bg, #7367f0);
  color: var(--btn-primary-text, #fff);
}

.btn-primary:hover {
  background: var(--btn-primary-bg-hover, #5f54d6);
  box-shadow: var(--btn-primary-shadow-hover, 0 4px 12px rgba(115,103,240,0.3));
}

.btn-secondary {
  background: #f5f5f5;
  color: #333;
}

.btn-secondary:hover {
  background: #e9e9e9;
}

.btn-sm {
  padding: 0.4rem 0.8rem;
  font-size: 0.75rem;
}

.export-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e0e0e0;
}

.export-section h4 {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
}

.export-section textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: monospace;
  font-size: 0.75rem;
  resize: vertical;
  margin-bottom: 0.5rem;
}

.preview-panel {
  overflow-y: auto;
  max-height: 90vh;
}

.preview-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #f0f0f0;
}

.preview-section:last-child {
  border-bottom: none;
}

.preview-section h4 {
  margin: 0 0 1rem;
  font-size: 0.9rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-btn {
  padding: 0.6rem 1.5rem;
  border-radius: 6px;
  border: none;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  margin-right: 0.75rem;
  transition: all 0.2s;
}

.preview-btn--primary {
  background: var(--btn-primary-bg);
  color: var(--btn-primary-text);
}

.preview-btn--primary:hover {
  background: var(--btn-primary-bg-hover);
  box-shadow: var(--btn-primary-shadow-hover);
}

.preview-btn--outline {
  background: transparent;
  border: 2px solid var(--btn-primary-bg);
  color: var(--btn-primary-bg);
}

.preview-btn--outline:hover {
  background: var(--primary-subtle-bg);
}

.preview-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: var(--badge-primary-bg);
  color: var(--badge-primary-text);
  margin-right: 0.5rem;
}

.preview-badge--light {
  background: var(--badge-primary-light-bg);
  color: var(--badge-primary-light-text);
}

.preview-input {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.preview-input:focus {
  outline: none;
  border-color: var(--input-border-focus);
  box-shadow: var(--input-shadow-focus);
  background: var(--input-bg-focus);
}

.preview-card {
  border: 2px solid var(--card-accent-border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--card-accent-bg);
  transition: all 0.2s;
}

.preview-card:hover {
  box-shadow: var(--card-hover-shadow);
}

.preview-card__header {
  padding: 0.75rem 1rem;
  background: var(--brand-primary);
  color: var(--brand-primary-text);
  font-weight: 500;
}

.preview-card__body {
  padding: 1rem;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: var(--header-bg);
  color: var(--header-text);
  border-radius: 8px;
  box-shadow: var(--header-shadow);
}

.header-logo-container {
  display: flex;
  align-items: center;
  height: 40px;
}

.header-logo {
  max-height: 40px;
  max-width: 120px;
}

.header-logo-placeholder {
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1.1rem;
}

.preview-header__active {
  background: var(--header-bg-hover);
  font-weight: 500;
}

.header-mode-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

@media (max-width: 1024px) {
  .theme-config {
    grid-template-columns: 1fr;
  }
}
</style>
